import { Form, Modal, Button } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CandidatoService } from '../../candidato.service';
import { EMAIL_INSTITUCIONAL_DOMINIO } from '../../candidato.constants';
import { Links } from '@/shared/constants/navegacao';
import { TipoLotacaoEstagioList } from '@/shared/constants/enumlist';

const candidatoService = new CandidatoService();

export type PromoverPreview = {
  candidatoId: number;
  pessoaId: string;
  nome: string;
  cpf?: string;
  emailPessoal?: string;
  cursoId?: number;
  instituicaoEnsinoId?: number;
  instituicaoEnsinoNome?: string;
  comarcaPretendidaId?: number;
  data_inicio_estagio_sugerida?: string;
};

export type PromoverPosAcao = 'edit' | 'lista';

type Props = {
  candidatoId: number;
  show: boolean;
  onHide: () => void;
  onSuccess?: (pessoaId: string, action: PromoverPosAcao) => void;
};

export function PromoverEstagiarioModal({ candidatoId, show, onHide, onSuccess }: Props) {
  const [preview, setPreview] = useState<PromoverPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tipoEstagio, setTipoEstagio] = useState(String(TipoLotacaoEstagioList[0].value));
  const [emailInstitucional, setEmailInstitucional] = useState(EMAIL_INSTITUCIONAL_DOMINIO);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [fimSemana, setFimSemana] = useState(false);

  useEffect(() => {
    if (!show || candidatoId <= 0) return;
    setLoading(true);
    candidatoService
      .getPromoverPreview(candidatoId)
      .then((p) => {
        setPreview(p);
        setDataInicio(p.data_inicio_estagio_sugerida ?? '');
        setEmailInstitucional(EMAIL_INSTITUCIONAL_DOMINIO);
      })
      .catch((e) => {
        toast.error(e?.message || 'Falha ao carregar preview.');
        onHide();
      })
      .finally(() => setLoading(false));
  }, [show, candidatoId, onHide]);

  const executarPromocao = async (action: PromoverPosAcao) => {
    const email = emailInstitucional.trim();
    const localPart = email.split('@')[0]?.trim();
    if (!localPart || !dataInicio) {
      toast.warn('Preencha o usuário do e-mail institucional e a data de início.');
      return;
    }
    setSaving(true);
    try {
      const { pessoaId } = await candidatoService.promoverEstagiario(candidatoId, {
        tipo_estagio: Number(tipoEstagio),
        email: email,
        data_inicio_estagio: dataInicio,
        data_fim_estagio: dataFim || undefined,
        fim_semana: fimSemana,
      });
      toast.success('Candidato promovido a estagiário.');
      onHide();
      onSuccess?.(pessoaId, action);
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao promover candidato.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    void executarPromocao('edit');
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Promover a estagiário</Modal.Title>
      </Modal.Header>
      <Form onSubmit={(ev) => void handleSubmit(ev)}>
        <Modal.Body>
          {loading ? (
            <p className="text-muted mb-0">Carregando…</p>
          ) : preview ? (
            <>
              <p className="small text-muted">
                Dados herdados da inscrição: <strong>{preview.nome}</strong>
                {preview.cpf ? ` — CPF ${preview.cpf}` : ''}
              </p>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label>E-mail institucional do estágio</Form.Label>
                  <Form.Control
                    type="email"
                    value={emailInstitucional}
                    onChange={(e) => setEmailInstitucional(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Tipo de estágio</Form.Label>
                  <Form.Select value={tipoEstagio} onChange={(e) => setTipoEstagio(e.target.value)}>
                    {TipoLotacaoEstagioList.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-6">
                  <Form.Label>Data início</Form.Label>
                  <Form.Control
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Data fim (opcional)</Form.Label>
                  <Form.Control type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
                </div>
                <div className="col-12">
                  <Form.Check
                    type="checkbox"
                    label="Estágio inclui fim de semana"
                    checked={fimSemana}
                    onChange={(e) => setFimSemana(e.target.checked)}
                  />
                </div>
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            disabled={loading || saving || !preview}
            onClick={() => void executarPromocao('lista')}
          >
            {saving ? 'Salvando…' : 'Promover e voltar à lista'}
          </Button>
          <Button type="submit" variant="primary" disabled={loading || saving || !preview}>
            {saving ? 'Salvando…' : 'Promover e Editar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export function linkPessoaEstagiario(pessoaId: string) {
  return `${Links.Pessoa_Editar}/${pessoaId}`;
}

export function linkCandidatosProcessoSeletivo(processoSeletivoId: number) {
  return `${Links.Candidato_Index}/${processoSeletivoId}`;
}
